import { Resolver, Mutation, Arg, Ctx, Directive } from 'type-graphql'
import { In } from 'typeorm'
import { getRepository } from '@things-factory/shell'
import { DataSet } from '@things-factory/dataset'

import { createAttachment, deleteAttachmentsByRef } from '@things-factory/attachment-base'
import { KpiMetric } from './kpi-metric'
import { NewKpiMetric, KpiMetricPatch } from './kpi-metric-type'
import { KpiFormulaService } from '../kpi/kpi-formula.service'
import { registerSchedule, unregisterSchedule, CallbackBase, Application } from '@things-factory/scheduler-client'

@Resolver(KpiMetric)
export class KpiMetricMutation {
  @Directive('@transaction')
  @Directive('@privilege(category: "kpi", privilege: "mutation", domainOwnerGranted: true, superUserGranted: true)')
  @Mutation(returns => KpiMetric, { description: 'Create a new KPI metric with the provided details.' })
  async createKpiMetric(
    @Arg('kpiMetric', { description: 'Input object containing details for the new KPI metric.' })
    kpiMetric: NewKpiMetric,
    @Ctx() context: ResolverContext
  ): Promise<KpiMetric> {
    const { domain, user, tx } = context.state

    let dataSet = kpiMetric.dataSet?.id
      ? await getRepository(DataSet).findOne({ where: { id: kpiMetric.dataSet.id } })
      : undefined

    // formula 관련 로직 완전 제거

    const result = await getRepository(KpiMetric, tx).save({
      ...kpiMetric,
      dataSet,
      domain,
      creator: user,
      updater: user
    })

    // 스케줄러 등록
    if (kpiMetric.schedule) {
      const handle = await registerSchedule({
        name: kpiMetric.name,
        client: {
          application: Application,
          group: `${domain.id}`,
          type: 'kpi-metric',
          key: result.id,
          operation: 'aggregate'
        },
        type: 'cron',
        schedule: kpiMetric.schedule,
        timezone: kpiMetric.timezone,
        task: {
          type: 'rest',
          connection: {
            host: `${CallbackBase}/callback-schedule-for-kpi-metric`,
            headers: {
              'Content-Type': 'application/json',
              accept: '*/*'
            }
          },
          data: {
            domainId: domain.id,
            metricId: result.id
          },
          history_check: true,
          failed_policy: 'retry_dlq',
          max_retry_count: 3,
          retry_period: 60
        }
      })
      result.scheduleId = handle
      result.timezone = kpiMetric.timezone
      await getRepository(KpiMetric, tx).save(result)
    }

    if (kpiMetric.thumbnail) {
      await createAttachment(
        null,
        {
          attachment: {
            file: kpiMetric.thumbnail,
            refType: KpiMetric.name,
            refBy: result.id
          }
        },
        context
      )
    }

    return result
  }

  @Directive('@transaction')
  @Directive('@privilege(category: "kpi", privilege: "mutation", domainOwnerGranted: true, superUserGranted: true)')
  @Mutation(returns => KpiMetric, { description: 'To modify KpiMetric information' })
  async updateKpiMetric(
    @Arg('id') id: string,
    @Arg('patch') patch: KpiMetricPatch,
    @Ctx() context: ResolverContext
  ): Promise<KpiMetric> {
    const { domain, user, tx } = context.state

    const repository = getRepository(KpiMetric, tx)
    const kpiMetric = await repository.findOne({
      where: { domain: { id: domain.id }, id }
    })

    const dsId = patch.dataSet?.id
    let dataSet = dsId ? await getRepository(DataSet, tx).findOne({ where: { id: dsId } }) : kpiMetric.dataSet

    delete patch.dataSet

    const result = await repository.save({
      ...kpiMetric,
      ...patch,
      dataSet,
      updater: user
    })

    // 스케줄러 해제/등록 (변경 시)
    if (kpiMetric.scheduleId && (patch.scheduleId !== kpiMetric.scheduleId || !patch.scheduleId)) {
      await unregisterSchedule(kpiMetric.scheduleId)
      result.scheduleId = null
    }
    if (patch.schedule) {
      const handle = await registerSchedule({
        name: patch.name || kpiMetric.name,
        client: {
          application: Application,
          group: `${domain.id}`,
          type: 'kpi-metric',
          key: result.id,
          operation: 'aggregate'
        },
        type: 'cron',
        schedule: patch.schedule,
        timezone: patch.timezone,
        task: {
          type: 'rest',
          connection: {
            host: `${CallbackBase}/callback-schedule-for-kpi-metric`,
            headers: {
              'Content-Type': 'application/json',
              accept: '*/*'
            }
          },
          data: {
            domainId: domain.id,
            metricId: result.id
          },
          history_check: true,
          failed_policy: 'retry_dlq',
          max_retry_count: 3,
          retry_period: 60
        }
      })
      result.scheduleId = handle
      result.timezone = patch.timezone
      await repository.save(result)
    }

    if (patch.thumbnail) {
      await deleteAttachmentsByRef(null, { refBys: [result.id] }, context)
      await createAttachment(
        null,
        {
          attachment: {
            file: patch.thumbnail,
            refType: KpiMetric.name,
            refBy: result.id
          }
        },
        context
      )
    }

    return result
  }

  @Directive('@transaction')
  @Directive('@privilege(category: "kpi", privilege: "mutation", domainOwnerGranted: true, superUserGranted: true)')
  @Mutation(returns => [KpiMetric], { description: "To modify multiple KpiMetrics' information" })
  async updateMultipleKpiMetric(
    @Arg('patches', type => [KpiMetricPatch]) patches: KpiMetricPatch[],
    @Ctx() context: ResolverContext
  ): Promise<KpiMetric[]> {
    const { domain, user, tx } = context.state

    let results = []
    const _createRecords = patches.filter((patch: any) => patch.cuFlag.toUpperCase() === '+')
    const _updateRecords = patches.filter((patch: any) => patch.cuFlag.toUpperCase() === 'M')
    const kpiMetricRepo = getRepository(KpiMetric, tx)

    if (_createRecords.length > 0) {
      for (let i = 0; i < _createRecords.length; i++) {
        const newRecord = _createRecords[i]

        const dsId = newRecord.dataSet?.id
        let dataSet = dsId ? await getRepository(DataSet, tx).findOne({ where: { id: dsId } }) : undefined

        delete newRecord.dataSet

        const result = await kpiMetricRepo.save({
          ...newRecord,
          dataSet,
          domain,
          creator: user,
          updater: user
        })

        if (newRecord.thumbnail) {
          await createAttachment(
            null,
            {
              attachment: {
                file: newRecord.thumbnail,
                refType: KpiMetric.name,
                refBy: result.id
              }
            },
            context
          )
        }

        results.push({ ...result, cuFlag: '+' })
      }
    }

    if (_updateRecords.length > 0) {
      for (let i = 0; i < _updateRecords.length; i++) {
        const updateRecord = _updateRecords[i]
        const kpiMetric = await kpiMetricRepo.findOne({ where: { id: updateRecord.id }, relations: ['dataSet'] })

        const dsId = updateRecord.dataSet?.id
        let dataSet = dsId ? await getRepository(DataSet, tx).findOne({ where: { id: dsId } }) : kpiMetric.dataSet

        // ObjectRef 필드 제거 (TypeORM에 실제 엔티티만 전달)
        delete updateRecord.dataSet

        const result = await kpiMetricRepo.save({
          ...kpiMetric,
          ...updateRecord,
          dataSet,
          updater: user
        })

        if (updateRecord.thumbnail) {
          await deleteAttachmentsByRef(null, { refBys: [result.id] }, context)
          await createAttachment(
            null,
            {
              attachment: {
                file: updateRecord.thumbnail,
                refType: KpiMetric.name,
                refBy: result.id
              }
            },
            context
          )
        }

        results.push({ ...result, cuFlag: 'M' })
      }
    }

    return results
  }

  @Directive('@transaction')
  @Directive('@privilege(category: "kpi", privilege: "mutation", domainOwnerGranted: true, superUserGranted: true)')
  @Mutation(returns => Boolean, { description: 'To delete KpiMetric' })
  async deleteKpiMetric(@Arg('id') id: string, @Ctx() context: ResolverContext): Promise<boolean> {
    const { domain, tx } = context.state
    const repo = getRepository(KpiMetric, tx)
    const metric = await repo.findOne({ where: { domain: { id: domain.id }, id } })
    if (metric?.scheduleId) {
      await unregisterSchedule(metric.scheduleId)
    }
    await repo.delete({ domain: { id: domain.id }, id })
    await deleteAttachmentsByRef(null, { refBys: [id] }, context)

    return true
  }

  @Directive('@transaction')
  @Directive('@privilege(category: "kpi", privilege: "mutation", domainOwnerGranted: true, superUserGranted: true)')
  @Mutation(returns => Boolean, { description: 'To delete multiple KpiMetrics' })
  async deleteKpiMetrics(
    @Arg('ids', type => [String]) ids: string[],
    @Ctx() context: ResolverContext
  ): Promise<boolean> {
    const { domain, tx } = context.state

    await getRepository(KpiMetric, tx).delete({
      domain: { id: domain.id },
      id: In(ids)
    })

    await deleteAttachmentsByRef(null, { refBys: ids }, context)

    return true
  }

  @Directive('@transaction')
  @Directive('@privilege(category: "kpi", privilege: "mutation", domainOwnerGranted: true, superUserGranted: true)')
  @Mutation(returns => Boolean, { description: 'To import multiple KpiMetrics' })
  async importKpiMetrics(
    @Arg('kpiMetrics', type => [KpiMetricPatch]) kpiMetrics: KpiMetricPatch[],
    @Ctx() context: ResolverContext
  ): Promise<boolean> {
    const { domain, tx } = context.state

    await Promise.all(
      kpiMetrics.map(async (kpiMetric: KpiMetricPatch) => {
        const createdKpiMetric: KpiMetric = await getRepository(KpiMetric, tx).save({ domain, ...kpiMetric })
      })
    )

    return true
  }
}
